import { useState, useEffect } from "react";
import { searchMessages, getTicket, isEndpointMissing } from "../../../services/aiChatService";
import { parseTicketQuery } from "../../../components/helpdesk/tickets";

// Búsqueda en el contenido de los mensajes (server-side).
export const SEARCH_MIN_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 350;

/**
 * Búsquedas server-side de la bandeja a partir del texto del buscador: en el
 * contenido de los mensajes y, si la consulta ES un ticket, el ticket exacto.
 * Los derivados que dependen de la bandeja activa o de la lista los calcula
 * useHelpdeskInbox.
 * @param {string} searchQuery
 */
export function useInboxServerSearch(searchQuery) {
  // Búsqueda dentro del contenido de los mensajes (server-side).
  const [messageResults, setMessageResults] = useState([]);
  const [messageSearchLoading, setMessageSearchLoading] = useState(false);
  // Una vez que el endpoint responde 404/405 dejamos de pedirlo: la sección
  // desaparece y la búsqueda queda igual que antes (solo filtro local).
  const [messageSearchUnavailable, setMessageSearchUnavailable] = useState(false);
  // Fallo transitorio (5xx / red): a diferencia del 404/405 no se latchea, se
  // reintenta en la siguiente búsqueda; solo cambia el estado vacío para no
  // afirmar "ningún mensaje contiene ese texto" cuando nadie llegó a buscar.
  const [messageSearchError, setMessageSearchError] = useState(false);

  // Búsqueda por TICKET (server-side, endpoint propio). Solo se dispara cuando
  // la consulta ES un ticket ("#123", "t123", "ticket 123"): los dígitos solos
  // siguen siendo búsqueda de teléfono.
  const [ticketResult, setTicketResult] = useState(null);
  const [ticketLookupLoading, setTicketLookupLoading] = useState(false);
  // null | "notfound" (404: el ticket no existe) | "error" (red / 5xx).
  const [ticketLookupError, setTicketLookupError] = useState(null);
  // Número consultado: sin esto, el estado vacío del 404 no sabría qué número
  // nombrar (searchQuery puede seguir tecleándose).
  const [ticketLookupNumber, setTicketLookupNumber] = useState(null);

  // ── Búsqueda dentro de los mensajes (debounce 350ms, mínimo 2 caracteres) ──
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < SEARCH_MIN_CHARS || messageSearchUnavailable) {
      setMessageResults([]);
      setMessageSearchLoading(false);
      setMessageSearchError(false);
      return undefined;
    }

    let cancelled = false;
    setMessageSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { results } = await searchMessages({ q });
        if (cancelled) return;
        setMessageResults(results ?? []);
        setMessageSearchError(false);
      } catch (err) {
        if (cancelled) return;
        setMessageResults([]);
        if (isEndpointMissing(err)) setMessageSearchUnavailable(true);
        else setMessageSearchError(true);
      } finally {
        if (!cancelled) setMessageSearchLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery, messageSearchUnavailable]);

  // ── Búsqueda por ticket (debounce 350ms) ──────────────────────────────────
  // Mismo debounce que la búsqueda de mensajes: el ticket es una consulta
  // exacta, no hay razón para pegarle al backend en cada tecla.
  useEffect(() => {
    const number = parseTicketQuery(searchQuery);
    if (!number) {
      setTicketResult(null);
      setTicketLookupError(null);
      setTicketLookupNumber(null);
      setTicketLookupLoading(false);
      return undefined;
    }

    let cancelled = false;
    setTicketLookupNumber(number);
    setTicketLookupLoading(true);
    const timer = setTimeout(async () => {
      try {
        const ticket = await getTicket(number);
        if (cancelled) return;
        setTicketResult(ticket);
        // 404 (null) = el ticket no existe. Un error de red es otra cosa: la
        // fila dice que no se pudo buscar, no que el ticket no exista.
        setTicketLookupError(ticket ? null : "notfound");
      } catch {
        if (cancelled) return;
        setTicketResult(null);
        setTicketLookupError("error");
      } finally {
        if (!cancelled) setTicketLookupLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchQuery]);

  return {
    messageResults,
    messageSearchLoading,
    messageSearchUnavailable,
    messageSearchError,
    ticketResult,
    ticketLookupLoading,
    ticketLookupError,
    ticketLookupNumber,
  };
}
